import { Bell, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNotifications } from '@/hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';

export const NotificationCenter = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Helper: Extract order number from message (e.g., "ORD-2025-00001")
  const extractOrderNumber = (message: string): string | null => {
    const match = message.match(/ORD-\d{4}-\d{5}/);
    return match ? match[0] : null;
  };

  // Helper: Get order_id from order_number
  const getOrderIdByNumber = async (orderNumber: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('id')
        .eq('order_number', orderNumber)
        .single();
      
      if (error) throw error;
      return data?.id || null;
    } catch (error) {
      console.error('Error fetching order by number:', error);
      return null;
    }
  };

  // Helper: Get order_id from transport_job_id
  const getOrderIdFromTransportJob = async (transportJobId: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase
        .from('transport_jobs')
        .select('order_id')
        .eq('id', transportJobId)
        .single();
      
      if (error) throw error;
      return data?.order_id || null;
    } catch (error) {
      console.error('Error fetching order from transport job:', error);
      return null;
    }
  };

  const handleNotificationClick = async (notification: any) => {
    if (isProcessing) return;
    
    setIsProcessing(true);

    try {
      // Mark as read if not already
      if (!notification.read_at) {
        await markAsRead(notification.id);
      }

      let targetPath: string | null = null;

      // Determine navigation based on notification type
      switch (notification.type) {
        // SHIPPER NOTIFICATIONS
        case 'new_bid': {
          const orderNumber = extractOrderNumber(notification.message);
          if (orderNumber) {
            const orderId = await getOrderIdByNumber(orderNumber);
            if (orderId) {
              targetPath = `/shipper/order-bids?orderId=${orderId}`;
            }
          }
          break;
        }

        case 'new_reservation':
          targetPath = '/shipper/dashboard';
          break;

        case 'delay_reported': {
          if (notification.related_job_id) {
            const orderId = await getOrderIdFromTransportJob(notification.related_job_id);
            if (orderId) {
              targetPath = `/shipper/tracking/${orderId}`;
            }
          }
          break;
        }

        // TRANSPORTER NOTIFICATIONS
        case 'bid_accepted':
        case 'mission_accepted':
          targetPath = '/transporter/missions';
          break;

        case 'bid_declined':
        case 'mission_declined':
          targetPath = '/transporter/marketplace';
          break;

        // DRIVER NOTIFICATIONS
        case 'mission_assigned':
          targetPath = '/driver/missions';
          break;

        default:
          console.warn('Notification type not handled:', notification.type);
      }

      if (targetPath) {
        setOpen(false);
        navigate(targetPath);
      } else {
        toast.error(t('notifications.notificationNotFound'));
      }
    } catch (error: any) {
      console.error('Error handling notification click:', error);
      toast.error(t('notifications.markAsReadError'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      toast.success(t('notifications.allMarkedAsRead'));
    } catch (error: any) {
      toast.error(t('notifications.markAllAsReadError'));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-lg hover:bg-[#EBF4F8] text-[#5A6B7A] hover:text-[#0880AE]"
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF7171] text-white text-xs font-semibold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 sm:w-96 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b border-[#DBE2EA]">
          <h3 className="text-lg font-semibold text-[#37699E] font-heading">
            {t('notifications.title')}
          </h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="text-xs text-[#0880AE] hover:text-[#076A8E] hover:bg-[#EBF4F8]"
            >
              <Check size={14} className="mr-1" />
              {t('notifications.markAll')}
            </Button>
          )}
        </div>

        <ScrollArea className="h-[400px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Bell size={48} className="text-[#DBE2EA] mb-4" />
              <p className="text-sm text-[#5A6B7A]">{t('notifications.empty')}</p>
            </div>
          ) : (
            <div className="divide-y divide-[#DBE2EA]">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-[#F8F9FA] transition-colors cursor-pointer relative ${
                    !notification.read_at ? 'bg-[#0880AE]/5' : ''
                  } ${isProcessing ? 'opacity-50 pointer-events-none' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                        !notification.read_at ? 'bg-[#0880AE]' : 'bg-transparent'
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-[#2D3748] mb-1">
                        {notification.title}
                      </h4>
                      <p className="text-sm text-[#5A6B7A] mb-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-[#87ADD4]">
                        {formatDistanceToNow(new Date(notification.created_at), {
                          addSuffix: true,
                          locale: fr,
                        })}
                      </p>
                    </div>
                    {isProcessing && (
                      <Loader2 className="w-4 h-4 animate-spin text-[#0880AE]" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
